import React from 'react';
import {
    getBindingCatalog,
    type RuntimeBindingDescriptor,
} from '../runtime/dataServices/bindingCatalog';
import type { RuntimeResultLinkDescriptor } from '../runtime/dataServices/types';
import type {
    ElementSettingsFormField,
    ElementSettingsFormSchema,
    RenderContextKind,
} from './types';
import { formatSupportedSwarmTypes } from '../swarmCompatibility';

interface ResolveRuntimeBindingSettingsFormSchemaArgs {
    contextKind: RenderContextKind;
    settingsFormSchema: ElementSettingsFormSchema;
    valueKind?: RuntimeResultLinkDescriptor['valueKind'];
    allowEmptyBindingOption?: boolean;
    includeLinksRequiringArg?: boolean;
    listDescriptorsForContext?: (
        contextKind: RenderContextKind
    ) => readonly RuntimeBindingDescriptor[];
}

function createRuntimeBindingIdChoiceOptions(
    descriptors: readonly RuntimeBindingDescriptor[]
): Array<[string, string]> {
    return descriptors.map((descriptor): [string, string] => {
        const { label, supportedSwarmTypes } = descriptor.link;
        if (supportedSwarmTypes === null) {
            return [descriptor.bindingId, label];
        }

        return [
            descriptor.bindingId,
            interpolate(
                gettext('LABEL_WITH_SENSOR_TYPES'),
                {
                    label,
                    sensorTypes: formatSupportedSwarmTypes(supportedSwarmTypes),
                },
                true
            ),
        ];
    });
}

function withBindingIdChoiceOptions(
    settingsFormSchema: ElementSettingsFormSchema,
    options: Array<[string, string]>,
    allowEmptyBindingOption: boolean
): ElementSettingsFormSchema {
    const bindingIdOptions: Array<[string, string]> = allowEmptyBindingOption
        ? [['', gettext('SELECT_OPTION')], ...options]
        : options;
    return {
        ...settingsFormSchema,
        fields: settingsFormSchema.fields.map((field) =>
            field.name !== 'binding' || field.type !== 'group' || !field.fields
                ? field
                : {
                      ...field,
                      fields: field.fields.map((bindingField) =>
                          bindingField.name === 'bindingId' && bindingField.type === 'choice'
                              ? { ...bindingField, options: bindingIdOptions }
                              : bindingField
                      ),
                  }
        ),
    };
}

function resolveRuntimeBindingLinkOptionDescriptors(
    args: Pick<
        ResolveRuntimeBindingSettingsFormSchemaArgs,
        'contextKind' | 'valueKind' | 'listDescriptorsForContext'
    >
): readonly RuntimeBindingDescriptor[] {
    const { contextKind, listDescriptorsForContext, valueKind } = args;
    if (listDescriptorsForContext) {
        return listDescriptorsForContext(contextKind);
    }

    if (valueKind) {
        return getBindingCatalog().listForContextAndValueKind(contextKind, valueKind);
    }

    throw new Error(
        'Expected runtime binding schema resolution to define either "valueKind" or "listDescriptorsForContext".'
    );
}

// Keep runtime-binding schema option wiring on one route to avoid per-element drift.
export function resolveRuntimeBindingSettingsFormSchema({
    allowEmptyBindingOption = false,
    contextKind,
    includeLinksRequiringArg = false,
    settingsFormSchema,
    valueKind,
    listDescriptorsForContext,
}: ResolveRuntimeBindingSettingsFormSchemaArgs): ElementSettingsFormSchema {
    const descriptors = resolveRuntimeBindingLinkOptionDescriptors({
        contextKind,
        valueKind,
        listDescriptorsForContext,
    });
    const options = createRuntimeBindingIdChoiceOptions(
        includeLinksRequiringArg
            ? descriptors
            : descriptors.filter((descriptor) => descriptor.link.argPolicy !== 'required')
    );
    return withBindingIdChoiceOptions(settingsFormSchema, options, allowEmptyBindingOption);
}

export function createContainCoverChoiceOptions(): Array<[string, string]> {
    return [
        ['contain', gettext('CONTAIN')],
        ['cover', gettext('COVER')],
    ];
}

interface CreateRuntimeBindingGroupFieldArgs {
    includeArg: boolean;
    validateLinkOptions?: boolean;
}

export function createRuntimeBindingGroupField({
    includeArg,
    validateLinkOptions,
}: CreateRuntimeBindingGroupFieldArgs): ElementSettingsFormField {
    const bindingIdField: ElementSettingsFormField = {
        name: 'bindingId',
        label: gettext('LINK'),
        type: 'choice',
        required: false,
        options: [],
        ...(typeof validateLinkOptions === 'undefined'
            ? {}
            : { validateOptions: validateLinkOptions }),
        default: '',
    };

    const fields: ElementSettingsFormField[] = [bindingIdField];

    if (includeArg) {
        fields.push({
            name: 'arg',
            label: gettext('ARGUMENT'),
            type: 'choice',
            options: [['', gettext('SELECT_OPTION')]],
            validateOptions: true,
            required: false,
            default: '',
        });
    }

    return {
        name: 'binding',
        label: gettext('RUNTIME_BINDING'),
        type: 'group',
        required: false,
        saveAsNullWhenMissingFields: ['bindingId'],
        default: null,
        fields,
    };
}

export function withRuntimeBindingArgChoiceField(
    settingsFormSchema: ElementSettingsFormSchema,
    options: ReadonlyArray<readonly [string, string]>
): ElementSettingsFormSchema {
    const normalizedOptions: Array<[string, string]> = [
        ['', gettext('SELECT_OPTION')],
        ...options.map(([k, v]): [string, string] => [k, v]),
    ];
    const argField: ElementSettingsFormField = {
        name: 'arg',
        label: gettext('ARGUMENT'),
        type: 'choice',
        options: normalizedOptions,
        validateOptions: true,
        required: false,
        default: '',
    };
    return {
        ...settingsFormSchema,
        fields: settingsFormSchema.fields.map((field) =>
            field.name !== 'binding' || field.type !== 'group' || !field.fields
                ? field
                : { ...field, fields: [...field.fields, argField] }
        ),
    };
}

export function withRuntimeBindingArgChoiceOptions(
    settingsFormSchema: ElementSettingsFormSchema,
    options: ReadonlyArray<readonly [string, string]>
): ElementSettingsFormSchema {
    const normalizedOptions: Array<[string, string]> = [
        ['', gettext('SELECT_OPTION')],
        ...options.map(([value, label]): [string, string] => [value, label]),
    ];

    return {
        ...settingsFormSchema,
        fields: settingsFormSchema.fields.map((field) =>
            field.name !== 'binding' || field.type !== 'group' || !field.fields
                ? field
                : {
                      ...field,
                      fields: field.fields.map((bindingField) =>
                          bindingField.name === 'arg'
                              ? {
                                    ...bindingField,
                                    options: normalizedOptions,
                                    required: true,
                                    validateOptions: true,
                                }
                              : bindingField
                      ),
                  }
        ),
    };
}

export function renderRuntimeBindingPlaceholder(label: string): JSX.Element {
    return (
        <div className="rounded border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-xs text-gray-600">
            {label}
        </div>
    );
}
